import { useEffect, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

/**
 * Tiny "NEW" pill that auto-disappears after `daysVisible` days from the
 * `since` ISO date. Used to flag freshly-shipped UI / pages so users notice
 * what changed — but the badge fades away on its own so the UI doesn't
 * stay cluttered forever.
 *
 * Usage:
 *   <NewBadge since="2026-04-27" />              // global (all users)
 *   <NewBadge since="2026-04-27" featureId="route-health" />  // per-user
 *
 * Per-user mode: clicking the ✕ on the badge writes the featureId to
 * localStorage so it never shows again for that user (independent of the
 * 7-day window).
 *
 * Default visibility window: 7 days (configurable via daysVisible).
 */
const DEFAULT_DAYS = 7;
const STORAGE_KEY = 'smarto.newbadge.dismissed';

const _readDismissed = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Lightweight GC: drop entries older than 90 days so the map can't grow
    // unboundedly. The 90-day floor far exceeds the 7-day default visibility
    // window, so users who dismissed don't get re-nagged before the badge
    // would have auto-expired anyway.
    const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
    let pruned = false;
    for (const k of Object.keys(parsed)) {
      if (typeof parsed[k] === 'number' && parsed[k] < cutoff) {
        delete parsed[k];
        pruned = true;
      }
    }
    if (pruned) _writeDismissed(parsed);
    return parsed;
  } catch (_) { return {}; }
};

const _writeDismissed = (map) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch (_) {}
};

export const NewBadge = ({
  since,
  daysVisible = DEFAULT_DAYS,
  featureId = null,
  label = null,
  className = '',
  size = 'sm',
  dismissable = true,
}) => {
  const [show, setShow] = useState(false);
  const { t } = useLanguage();
  const resolvedLabel = label || t('new_badge_label');

  useEffect(() => {
    if (!since) { setShow(false); return; }
    // Time-window check
    const sinceTs = Date.parse(since);
    if (Number.isNaN(sinceTs)) { setShow(false); return; }
    const expiresAt = sinceTs + daysVisible * 24 * 60 * 60 * 1000;
    if (Date.now() > expiresAt) { setShow(false); return; }
    // Per-user dismissal check
    if (featureId) {
      const dismissed = _readDismissed();
      if (dismissed[featureId]) { setShow(false); return; }
    }
    setShow(true);
  }, [since, daysVisible, featureId]);

  if (!show) return null;

  const onDismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (featureId) {
      const next = _readDismissed();
      next[featureId] = Date.now();
      _writeDismissed(next);
      try { window.dispatchEvent(new Event('smarto:newbadge-dismissed')); } catch (_) {}
    }
    setShow(false);
  };

  const sizeCls = size === 'xs'
    ? 'text-[9px] px-1.5 py-0.5'
    : size === 'lg'
      ? 'text-xs px-2.5 py-1'
      : 'text-[10px] px-2 py-0.5';

  return (
    <span
      data-testid={featureId ? `new-badge-${featureId}` : 'new-badge'}
      className={`inline-flex items-center gap-1 rounded-full bg-orange-500 text-white font-bold uppercase tracking-wider shadow-sm align-middle ${sizeCls} ${className}`}
      title={`${resolvedLabel} · ${new Date(since).toLocaleDateString()}`}
    >
      <span className="inline-block w-1 h-1 rounded-full bg-white/90 animate-pulse" />
      {resolvedLabel}
      {dismissable && featureId && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss new badge"
          className="ml-1 -mr-0.5 opacity-70 hover:opacity-100 leading-none"
        >
          ×
        </button>
      )}
    </span>
  );
};

export default NewBadge;
